import React from 'react'
import { useState, useMemo, useEffect } from 'react'
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Collapse, Box, Typography, Button, Pagination,
  Divider, useTheme, useMediaQuery
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import supabase from '../../../supabaseClient';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { BarChart } from '@mui/x-charts/BarChart';
import { PRODUCTION_SOURCES, PRODUCTION_DISPLAY, computeAveragePerHour } from '../../../Utils/machineProductionStats';
import ReportPaceIndicator from '../../../Components/Manager/Attendance/ReportPaceIndicator';
import LiveProductionCounter from '../../../Components/Manager/Attendance/LiveProductionCounter';

dayjs.extend(isoWeek);

// Kolejność przycisków wyboru maszyny w tym raporcie. Klucze odpowiadają
// production_source z tabeli devices/PRODUCTION_SOURCES.
const MACHINE_KEYS = ['cutting_machine_logs', 'vector1', 'vector2'];

// Cechy specyficzne dla prezentacji w TYM raporcie (jednostka, dokładność,
// czy maszyna mierzy wymiary sztuk, czy pokazujemy tempo/h) — Vectory liczą
// sztuki (palety), nie m3, więc nie mają średnicy/długości ani skalibrowanego
// pod m3/h wskaźnika tempa.
const MACHINE_DISPLAY = {
  cutting_machine_logs: { ...PRODUCTION_DISPLAY.cutting_machine_logs, hasDimensions: true, hasPace: true },
  vector1: { ...PRODUCTION_DISPLAY.vector1, hasDimensions: false, hasPace: false },
  vector2: { ...PRODUCTION_DISPLAY.vector2, hasDimensions: false, hasPace: false },
};

const fmtQty = (value, machine) => `${(value || 0).toFixed(machine.decimals)} ${machine.unit}`;


//funkcja fitrowanie po datach
function getDateRangeForPeriod(period){
    const today = dayjs()
    let start = today

    if(period === 'day'){
        start = today
    }else if(period === 'week'){
        start = today.startOf('isoWeek');
    }else if(period ==='month'){
        start = today.startOf('month');
    }
    return {
        start: start.format('YYYY-MM-DD'),
        end: today.format('YYYY-MM-DD'),
    }
}

// pobiera wszystkie wiersze zapytania, obchodząc domyślny limit wierszy PostgREST/Supabase
async function fetchAllRows(query, pageSize = 1000) {
  let all = [];
  let from = 0;
  while (true) {
    const { data, error } = await query.range(from, from + pageSize - 1);
    if (error) {
      console.log('Błąd zapytania:', error);
      return null;
    }
    all = all.concat(data);
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

// scalanie nakładających się/stykających się przedziałow czasu [{start, end}] (dayjs)
function mergeIntervals(intervals) {
  const sorted = [...intervals].sort((a, b) => a.start.valueOf() - b.start.valueOf());
  const merged = [];
  for (const cur of sorted) {
    const last = merged[merged.length - 1];
    if (last && cur.start.valueOf() <= last.end.valueOf()) {
      if (cur.end.valueOf() > last.end.valueOf()) last.end = cur.end;
    } else {
      merged.push({ start: cur.start, end: cur.end });
    }
  }
  return merged;
}

// minuty nakładania się przedziału [aStart, aEnd] z listą scalonych przedziałów pracy
function overlapMinutes(bucketStart, bucketEnd, mergedIntervals) {
  let minutes = 0;
  for (const { start, end } of mergedIntervals) {
    const from = start.isAfter(bucketStart) ? start : bucketStart;
    const to = end.isBefore(bucketEnd) ? end : bucketEnd;
    const diff = to.diff(from, 'minute', true);
    if (diff > 0) minutes += diff;
  }
  return minutes;
}

// buduje przedziały pracy [created_at - 5min, created_at] dla logów z danego dnia (obcięte do granic doby)
function buildWorkIntervalsForDay(logs, dateStr) {
  const dayStart = dayjs(dateStr).startOf('day');
  const dayEnd = dayStart.add(1, 'day');
  const intervals = logs
    .filter((log) => dayjs(log.created_at).format('YYYY-MM-DD') === dateStr)
    .map((log) => {
      const end = dayjs(log.created_at);
      const start = end.subtract(5, 'minute');
      return {
        start: start.isBefore(dayStart) ? dayStart : start,
        end: end.isAfter(dayEnd) ? dayEnd : end,
      };
    });
  return mergeIntervals(intervals);
}

// tryb "Dzień": 21 słupków godzinowych 02:00-22:00
function computeHourlyBuckets(logs, dateStr, valueColumn) {
  const merged = buildWorkIntervalsForDay(logs, dateStr);
  const dayLogs = logs.filter((log) => dayjs(log.created_at).format('YYYY-MM-DD') === dateStr);

  const labels = [];
  const workMinutes = [];
  const idleMinutes = [];
  const meta = [];
  for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) {
    const bucketStart = dayjs(dateStr).startOf('day').hour(h);
    const bucketEnd = bucketStart.add(1, 'hour');
    const worked = Math.min(60, overlapMinutes(bucketStart, bucketEnd, merged));
    const hourQty = dayLogs
      .filter((log) => {
        const t = dayjs(log.created_at);
        return !t.isBefore(bucketStart) && t.isBefore(bucketEnd);
      })
      .reduce((sum, log) => sum + (log[valueColumn] || 0), 0);

    labels.push(bucketStart.format('HH:mm'));
    workMinutes.push(Math.round(worked));
    idleMinutes.push(Math.round(60 - worked));
    meta.push({ date: dateStr, qty: hourQty });
  }
  return { labels, workMinutes, idleMinutes, meta };
}

// tryb "Tydzień/Miesiąc/custom": jeden słupek na dzień, jednostka godziny (0-24h)
function computeDailyBuckets(logs, startDate, endDate, valueColumn) {
  const labels = [];
  const workHours = [];
  const idleHours = [];
  const meta = [];
  let cursor = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');
  while (cursor.isSame(end) || cursor.isBefore(end)) {
    const dateStr = cursor.format('YYYY-MM-DD');
    const merged = buildWorkIntervalsForDay(logs, dateStr);
    const dayStart = cursor;
    const dayEnd = cursor.add(1, 'day');
    const workedMinutes = Math.min(1440, overlapMinutes(dayStart, dayEnd, merged));
    const dayTotalQty = logs
      .filter((log) => dayjs(log.created_at).format('YYYY-MM-DD') === dateStr)
      .reduce((sum, log) => sum + (log[valueColumn] || 0), 0);

    labels.push(cursor.format('DD-MM'));
    workHours.push(+(workedMinutes / 60).toFixed(2));
    idleHours.push(+((1440 - workedMinutes) / 60).toFixed(2));
    meta.push({ date: dateStr, qty: dayTotalQty });

    cursor = cursor.add(1, 'day');
  }
  return { labels, workHours, idleHours, meta };
}

// Properties wykresu
const WORK_COLOR = '#6B1D2F';   // bordowy - praca
const IDLE_COLOR = '#EAD9C9';   // jasny beż - przestój
const DAY_START_HOUR = 2;
const DAY_END_HOUR = 22; // ostatni słupek to przedział 22:00-23:00

function MachineOperationReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [idConfiguration, setIdConfiguration] = useState('');
  const [activeMachine, setActiveMachine] = useState('cutting_machine_logs');
  const machine = { key: activeMachine, ...PRODUCTION_SOURCES[activeMachine], ...MACHINE_DISPLAY[activeMachine] };
  const [logs, setLogs] = useState([]);
  console.log('test isoWeek:', dayjs().startOf('isoWeek').format('dddd, YYYY-MM-DD'));
  console.log('test month:', dayjs().startOf('month').format('YYYY-MM-DD'));

  useEffect(() => {
    const init = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) return;
      const userId = sessionData.session.user.id;
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id_configuration')
        .eq('id', userId)
        .single();
      if (profileError || !profileData) {
        console.log('Błąd pobierania profilu:', profileError);
        return;
      }
      setIdConfiguration(profileData.id_configuration);
    };
    init();
  }, []);

  // Lista logów pod wykresem — zwijana, pobierana stronami (range), żeby nie
  // ciągnąć za jednym razem całej tabeli przy dłuższych zakresach dat.
  const LOGS_PAGE_SIZE = 25;
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [pagedLogs, setPagedLogs] = useState([]);
  const [logsTotalCount, setLogsTotalCount] = useState(0);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // filtr
  const initialRange = getDateRangeForPeriod('day');
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [activePreset, setActivePreset] = useState('day'); // tylko podświetlenie przycisku

  const totalProduction = logs.reduce((suma, log) => suma + (log[machine.valueColumn] || 0), 0);

  const averageDiameter = machine.hasDimensions ? (() => {
    const values = logs
      .flatMap((log) => [log.val_2, log.val_3, log.val_4])
      .filter((v) => v !== null && v !== undefined);
    return values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length / 10 : 0;
  })() : null;

  const averageLength = machine.hasDimensions ? (() => {
    const values = logs
      .map((log) => log.val_1)
      .filter((v) => v !== null && v !== undefined);
    return values.length > 0 ? values.reduce((sum, v) => sum + v, 0) / values.length / 10 : 0;
  })() : null;

  const [previousProduction, setPreviousProduction] = useState(null);
  const percentChange = previousProduction
    ? ((totalProduction - previousProduction) / previousProduction) * 100
    : null;

  const averagePerHour = useMemo(
    () => computeAveragePerHour(logs, machine.valueColumn, 'created_at'),
    [logs, machine.valueColumn]
  );

  // maszyna uznawana za pracującą, gdy ostatni log wpłynął w ciągu ostatnich 5 minut
  // — ta sama granica, co przy budowaniu przedziałów pracy (buildWorkIntervalsForDay)
  const isMachineWorking = useMemo(() => {
    if (logs.length === 0) return false;
    const last = logs[logs.length - 1];
    return dayjs().diff(dayjs(last.created_at), 'minute') <= 5;
  }, [logs]);

  const isDayMode = activePreset === 'day' || startDate === endDate;
  const chartData = useMemo(() => {
    if (isDayMode) {
      const { labels, workMinutes, idleMinutes, meta } = computeHourlyBuckets(logs, startDate, machine.valueColumn);
      return { labels, work: workMinutes, idle: idleMinutes, meta, unit: 'min' };
    }
    const { labels, workHours, idleHours, meta } = computeDailyBuckets(logs, startDate, endDate, machine.valueColumn);
    return { labels, work: workHours, idle: idleHours, meta, unit: 'h' };
  }, [logs, startDate, endDate, isDayMode, machine.valueColumn]);

  const chartWidth = isDayMode
    ? '100%'
    : Math.min(1200, Math.max(400, chartData.labels.length * 110));

  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const [clickedIndex, setClickedIndex] = useState(null);

  // wartości robocze pól "Od"/"Do" — niezależne od startDate/endDate, żeby pisanie/wybieranie
  // daty nie odpalało od razu zapytania; commitowane dopiero po kliknięciu "Pokaż"
  const [draftStartDate, setDraftStartDate] = useState(startDate);
  const [draftEndDate, setDraftEndDate] = useState(endDate);

  const draftStartValue = useMemo(() => dayjs(draftStartDate), [draftStartDate]);
  const draftEndValue = useMemo(() => dayjs(draftEndDate), [draftEndDate]);

  // trzyma pola "Od"/"Do" w zgodzie z aktualnym zakresem, gdy zmienia się on
  // z innego źródła niż same pola (presety Dzień/Tydzień/Miesiąc, strzałki)
  useEffect(() => {
    setDraftStartDate(startDate);
    setDraftEndDate(endDate);
  }, [startDate, endDate]);

  const isDraftRangeValid =
    dayjs(draftStartDate).isValid() &&
    dayjs(draftEndDate).isValid() &&
    !dayjs(draftEndDate).isBefore(dayjs(draftStartDate));

  function applyDraftRange() {
    if (!isDraftRangeValid) return;
    setStartDate(dayjs(draftStartDate).format('YYYY-MM-DD'));
    setEndDate(dayjs(draftEndDate).format('YYYY-MM-DD'));
    setActivePreset('custom');
  }

//funkcja zmiany dat dla arrowBack / arrowFollow
function shiftRange(direction) {
  if (activePreset === 'day') {
    const newDate = dayjs(startDate).add(direction, 'day');
    setStartDate(newDate.format('YYYY-MM-DD'));
    setEndDate(newDate.format('YYYY-MM-DD'));
  } else if (activePreset === 'week') {
    const targetWeek = dayjs(startDate).add(direction, 'week');
    setStartDate(targetWeek.startOf('isoWeek').format('YYYY-MM-DD'));
    setEndDate(targetWeek.endOf('isoWeek').format('YYYY-MM-DD'));
  } else {
    const targetMonth = dayjs(startDate).add(direction, 'month');
    setStartDate(targetMonth.startOf('month').format('YYYY-MM-DD'));
    setEndDate(targetMonth.endOf('month').format('YYYY-MM-DD'));
    setActivePreset('month');
  }
}

  useEffect(() => {
    if (!idConfiguration) return;
    let ignore = false;
    async function fetchMachineLogs() {
      const columns = machine.hasDimensions
        ? 'created_at, val_1, val_2, val_3, val_4, val_5, id_configuration'
        : 'created_at, val_1, id_configuration';
      const data = await fetchAllRows(
        supabase
          .from(machine.table)
          .select(columns)
          .eq('id_configuration', idConfiguration)
          .gte('created_at', startDate)
          .lte('created_at', endDate + 'T23:59:59')
          .order('created_at', { ascending: true })
      );
      if (ignore) return;
      if (data === null) {
        console.log('Błąd zapytania: nie udało się pobrać logów');
        return;
      }
      setLogs(data);
    }
    fetchMachineLogs();
    return () => {
      ignore = true;
    };
  }, [startDate, endDate, idConfiguration, machine.table, machine.hasDimensions]);

  useEffect(() => {
    if (!idConfiguration) return;
    let ignore = false;
    async function fetchPreviousPeriod() {
      const rangeLengthDays = dayjs(endDate).diff(dayjs(startDate), 'day') + 1;
      const prevEnd = dayjs(startDate).subtract(1, 'day');
      const prevStart = prevEnd.subtract(rangeLengthDays - 1, 'day');

      const data = await fetchAllRows(
        supabase
          .from(machine.table)
          .select(machine.valueColumn)
          .eq('id_configuration', idConfiguration)
          .gte('created_at', prevStart.format('YYYY-MM-DD'))
          .lte('created_at', prevEnd.format('YYYY-MM-DD') + 'T23:59:59')
      );
      if (ignore) return;
      if (data === null) {
        console.log('Błąd zapytania (poprzedni okres)');
        setPreviousProduction(null);
        return;
      }
      setPreviousProduction(data.reduce((sum, log) => sum + (log[machine.valueColumn] || 0), 0));
    }
    fetchPreviousPeriod();
    return () => {
      ignore = true;
    };
  }, [startDate, endDate, idConfiguration, machine.table, machine.valueColumn]);

  useEffect(() => {
    setHoveredIndex(null);
    setClickedIndex(null);
  }, [startDate, endDate]);

  // reset strony logów przy zmianie zakresu/maszyny, żeby nie zostać na
  // nieistniejącej stronie po zawężeniu wyniku
  useEffect(() => {
    setLogsPage(1);
  }, [startDate, endDate, idConfiguration, machine.table]);

  // pobranie jednej strony logów — tylko gdy panel jest rozwinięty, żeby nie
  // odpytywać bazy, dopóki użytkownik nie poprosi o szczegóły
  useEffect(() => {
    if (!logsOpen || !idConfiguration) return;
    let ignore = false;
    async function fetchLogsPage() {
      setLoadingLogs(true);
      const columns = machine.hasDimensions
        ? 'created_at, val_1, val_2, val_3, val_4, val_5'
        : 'created_at, val_1';
      const from = (logsPage - 1) * LOGS_PAGE_SIZE;
      const to = from + LOGS_PAGE_SIZE - 1;
      const { data, count, error } = await supabase
        .from(machine.table)
        .select(columns, { count: 'exact' })
        .eq('id_configuration', idConfiguration)
        .gte('created_at', startDate)
        .lte('created_at', endDate + 'T23:59:59')
        .order('created_at', { ascending: false })
        .range(from, to);
      if (ignore) return;
      if (error) {
        console.log('Błąd zapytania (logi):', error);
        setLoadingLogs(false);
        return;
      }
      setPagedLogs(data || []);
      setLogsTotalCount(count || 0);
      setLoadingLogs(false);
    }
    fetchLogsPage();
    return () => {
      ignore = true;
    };
  }, [logsOpen, logsPage, startDate, endDate, idConfiguration, machine.table, machine.hasDimensions]);

// dane do komponentu wyświetlającego wielkość produkcji na dziś
  const [todayProduction, setTodayProduction] = useState(0);
  const [todayAverageDiameter, setTodayAverageDiameter] = useState(0);
  const [todayAverageLength, setTodayAverageLength] = useState(0);

  useEffect(() => {
    if (!idConfiguration) return;
    async function fetchToday() {
      const today = dayjs().format('YYYY-MM-DD');
      const data = await fetchAllRows(
        supabase
          .from(machine.table)
          .select(machine.valueColumn)
          .eq('id_configuration', idConfiguration)
          .gte('created_at', today)
          .lte('created_at', today + 'T23:59:59')
      );

      if (data === null) {
        console.log('Błąd zapytania (dziś)');
        return;
      }
      setTodayProduction(data.reduce((sum, log) => sum + (log[machine.valueColumn] || 0), 0));
    }
    fetchToday();
  }, [idConfiguration, machine.table, machine.valueColumn]);

  useEffect(() => {
    if (!idConfiguration || !machine.hasDimensions) { setTodayAverageDiameter(0); return; }
    async function fetchDiameter() {
      const today = dayjs().format('YYYY-MM-DD');
      const data = await fetchAllRows(
        supabase
          .from(machine.table)
          .select('val_2, val_3, val_4')
          .eq('id_configuration', idConfiguration)
          .gte('created_at', today)
          .lte('created_at', today + 'T23:59:59')
      );
      if ( data === null ) {
        console.log('Błąd zapytania (dziś)');
        return;
      }
      const allDiameters = data
        .flatMap((log) => [log.val_2, log.val_3, log.val_4])
        .filter((v) => v !== null && v !== undefined)
        const average =
          allDiameters.length >0
           ? allDiameters.reduce((sum, v) => sum +v,0) /allDiameters.length /10
           : 0;
          setTodayAverageDiameter(average);
    }
    fetchDiameter();
  }, [idConfiguration, machine.table, machine.hasDimensions]);

  useEffect(() => {
    if (!idConfiguration || !machine.hasDimensions) { setTodayAverageLength(0); return; }
    async function fetchLength() {
      const today = dayjs().format('YYYY-MM-DD');
      const data = await fetchAllRows(
        supabase
          .from(machine.table)
          .select('val_1')
          .eq('id_configuration', idConfiguration)
          .gte('created_at', today)
          .lte('created_at', today + 'T23:59:59')
      );
      if ( data === null ) {
        console.log('Błąd zapytania (dziś)');
        return;
      }
      const allLengths = data
        .flatMap((log) => [log.val_1])
        .filter((v) => v !== null && v !== undefined)
        const average =
          allLengths.length > 0
           ? allLengths.reduce((sum, v) => sum + v, 0) / allLengths.length / 10
           : 0;
          setTodayAverageLength(average);
    }
    fetchLength();
  }, [idConfiguration, machine.table, machine.hasDimensions]);

  return (
    <>
      {/* wybór maszyny po lewej + sekcja "Dziś" (produkcja bieżącego dnia + test na żywo) po
          prawej, niezależne od filtra dat poniżej */}
      <Box sx={{
        display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between',
        gap: { xs: 2, sm: 3 }, mt: { xs: 2, sm: 5 }, mb: { xs: 2, sm: 5 }, px: { xs: 2, sm: 4 },
      }}>
        <Box sx={{
          display: 'flex', flexWrap: 'wrap', justifyContent: 'center', flexShrink: 0, gap: 1,
        }}>
          {MACHINE_KEYS.map((key) => (
            <Button
              key={key}
              variant={activeMachine === key ? 'contained' : 'outlined'}
              onClick={() => setActiveMachine(key)}
              size="large"
              sx={{ px: 3, py: 1.25, fontSize: '0.95rem', borderRadius: 3 }}
            >
              {PRODUCTION_SOURCES[key].label}
            </Button>
          ))}
        </Box>

        <Paper sx={{
          p: 1.5, borderRadius: 5, flex: '0 0 auto',
          width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: 'none', sm: 460 },
          display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 2.5 },
        }}>
          <Box sx={{ flex: '0 0 auto', minWidth: 150 }}>
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>Dziś</Typography>
            <Typography sx={{ fontSize: '24px', fontWeight: 600 }}>
              {fmtQty(todayProduction, machine)}
            </Typography>
            {machine.hasDimensions && (
              <>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
                  Średnia średnica: {todayAverageDiameter.toFixed(2)}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                  Średnia długość: {todayAverageLength.toFixed(2)}
                </Typography>
              </>
            )}
          </Box>

          <Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem />

          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <LiveProductionCounter idConfiguration={idConfiguration} machine={machine} />
          </Box>
        </Paper>
      </Box>

      <Typography sx={{
        fontSize: '13px', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5,
        textAlign: 'center', mt: 4,
      }}>
        WYBRANY OKRES
      </Typography>

      <Box sx={{
        display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'center',
        gap: { xs: 2, sm: 0 }, px: { xs: 2, sm: 0 },
      }}>
        <Box sx={{
          display: 'flex', flexWrap: 'wrap',
          justifyContent: 'center',
          gap: { xs: 1, sm: 0 },
        }}>
          <Button
            variant={activePreset === 'day' ? 'contained' : 'outlined'}
            onClick={() => {
              const { start, end } = getDateRangeForPeriod('day');
              setStartDate(start);
              setEndDate(end);
              setActivePreset('day');
            }}
          >
            Dzień
          </Button>
          <Button
            variant={activePreset === 'week' ? 'contained' : 'outlined'}
            onClick={() => {
              const { start, end } = getDateRangeForPeriod('week');
              setStartDate(start);
              setEndDate(end);
              setActivePreset('week');
            }}
          >
            Tydzień
          </Button>
          <Button
            variant={activePreset === 'month' ? 'contained' : 'outlined'}
            onClick={() => {
              const { start, end } = getDateRangeForPeriod('month');
              setStartDate(start);
              setEndDate(end);
              setActivePreset('month');
            }}
          >
            Miesiąc
          </Button>
        </Box>

        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <IconButton size="small" onClick={() => shiftRange(-1)}>
                <ArrowBackIosNewIcon />
              </IconButton>

              <DatePicker
                label="Od"
                format="DD-MM-YYYY"
                value={draftStartValue}
                onChange={(newValue) => {
                  if (!newValue || !newValue.isValid()) return;
                  setDraftStartDate(newValue.format('YYYY-MM-DD'));
                }}
                sx={{ flex: 1, minWidth: 0 }}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />

              <DatePicker
                label="Do"
                format="DD-MM-YYYY"
                value={draftEndValue}
                onChange={(newValue) => {
                  if (!newValue || !newValue.isValid()) return;
                  setDraftEndDate(newValue.format('YYYY-MM-DD'));
                }}
                sx={{ flex: 1, minWidth: 0 }}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />

              <IconButton size="small" onClick={() => shiftRange(1)}>
                <ArrowForwardIosIcon />
              </IconButton>
            </Box>

            <Button
              variant="contained"
              size="small"
              disabled={!isDraftRangeValid}
              onClick={applyDraftRange}
              sx={{ width: '100%' }}
            >
              Pokaż
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2, ml: 3 }}>
            <IconButton
              size="small"
              onClick={()=> shiftRange(-1)}>
                <ArrowBackIosNewIcon />
            </IconButton>

            <DatePicker
              label="Od"
              format="DD-MM-YYYY"
              value={draftStartValue}
              onChange={(newValue) => {
                // newValue może być null/nieprawidłowe w trakcie ręcznego wpisywania daty
                if (!newValue || !newValue.isValid()) return;
                setDraftStartDate(newValue.format('YYYY-MM-DD')); // ISO — do zapytań i porównań
              }}
              sx={{ mt: 2, width: '100%' }}
              slotProps={{ textField: { size: 'small' } }}
            />

            <DatePicker
              label="Do"
              format="DD-MM-YYYY"
              value={draftEndValue}
              onChange={(newValue) => {
                if (!newValue || !newValue.isValid()) return;
                setDraftEndDate(newValue.format('YYYY-MM-DD'));
              }}
              sx={{ mt: 2, width: '100%' }}
              slotProps={{ textField: { size: 'small' } }}
            />

            <Button
              variant="contained"
              size="small"
              disabled={!isDraftRangeValid}
              onClick={applyDraftRange}
              sx={{ mt: 2 }}
            >
              Pokaż
            </Button>

            <IconButton
              size="small"
              onClick={()=>shiftRange(1)}>
                <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        )}
      </Box>

        {/* statystyki za wybrany okres (DZIEŃ/TYDZIEŃ/MIESIĄC/custom) — jeden pasek, spójny z sekcją "Dziś" */}
      <Box sx={{ px: { xs: 2, sm: 4 }, mt: 2 }}>
        <Paper sx={{
          p: 2, borderRadius: 5,
          display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 2, sm: 4 },
        }}>
          {machine.hasPace && (
            <>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ReportPaceIndicator
                  averagePerHour={averagePerHour}
                  total={totalProduction}
                  isWorking={isMachineWorking}
                  unit={`${machine.unit}/h`}
                  totalUnit={machine.unit}
                  label={machine.label.toUpperCase()}
                />
              </Box>
              <Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem />
            </>
          )}

          <Box sx={{ flex: '0 0 auto', minWidth: 220 }}>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
              od {startDate} do {endDate}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mt: 0.5 }}>
              <Typography sx={{ fontSize: '30px', fontWeight: 600 }}>{fmtQty(totalProduction, machine)}</Typography>
              {percentChange !== null && (
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: percentChange >= 0 ? 'green' : 'error.main',
                  }}
                >
                  {percentChange >= 0 ? '+' : ''}
                  {percentChange.toFixed(1)}%
                </Typography>
              )}
            </Box>
            {machine.hasDimensions && (
              <>
                <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: 0.5 }}>
                  Średnia średnica: {averageDiameter.toFixed(2)} cm
                </Typography>
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                  Średnia długość: {averageLength.toFixed(2)} cm
                </Typography>
              </>
            )}
          </Box>

          {/* statystyki wybranego słupka — tylko po kliknięciu */}
          {clickedIndex !== null && chartData.meta[clickedIndex] && (
            <>
              <Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem />
              <Box sx={{ flex: '0 0 auto', minWidth: 200 }}>
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                  {isDayMode ? `${chartData.meta[clickedIndex].date} ${chartData.labels[clickedIndex]}` : chartData.meta[clickedIndex].date}
                </Typography>
                <Typography sx={{ fontSize: '30px', fontWeight: 600, mt: 0.5 }}>
                  {fmtQty(chartData.meta[clickedIndex].qty, machine)}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: 0.5 }}>
                  Praca {chartData.work[clickedIndex]} {chartData.unit === 'min' ? 'min' : 'h'}
                </Typography>
                <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>
                  Przestój {chartData.idle[clickedIndex]} {chartData.unit === 'min' ? 'min' : 'h'}
                </Typography>
              </Box>
            </>
          )}
        </Paper>
      </Box>

      {/* wykres słupkowy praca/przestój — zewnętrzny kontener przewija w poziomie tylko tę sekcję,
          żeby szeroki wykres (tryb tydzień/miesiąc) nie psuł layoutu całej strony na mobile */}
      <Box sx={{ mt: 4, width: '100%', overflowX: 'auto', overflowY: 'hidden', pb: 1 }}>
      <Box
        sx={{ position: 'relative', mx: isDayMode ? 'auto' : 0, width: chartWidth, minWidth: isDayMode ? '100%' : chartWidth }}
        onMouseMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          setHoverPos({ x: event.clientX - rect.left, y: event.clientY - rect.top });
        }}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <BarChart
          xAxis={[{ scaleType: 'band', data: chartData.labels }]}
          yAxis={[{ label: chartData.unit === 'min' ? 'Minuty' : 'Godziny' }]}
          series={[
            { data: chartData.work, label: 'Praca', stack: 'total', color: WORK_COLOR },
            { data: chartData.idle, label: 'Przestój', stack: 'total', color: IDLE_COLOR },
          ]}
          height={300}
          borderRadius={4}
          categoryGapRatio={0.4}
          slotProps={{ tooltip: { trigger: 'none' } }}
          onHighlightChange={(highlighted) => setHoveredIndex(highlighted?.dataIndex ?? null)}
          onItemClick={(event, item) =>
            setClickedIndex((prev) => (prev === item.dataIndex ? null : item.dataIndex))
          }
        />
        {hoveredIndex !== null && chartData.meta[hoveredIndex] && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: hoverPos.y + 12,
              left: hoverPos.x + 12,
              p: 1.5,
              borderRadius: 3,
              pointerEvents: 'none',
              minWidth: 150,
              zIndex: 10,
            }}
          >
            <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
              {fmtQty(chartData.meta[hoveredIndex].qty, machine)}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>
              {chartData.labels[hoveredIndex]}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: WORK_COLOR }} />
              <Typography sx={{ fontSize: '13px' }}>
                Praca {chartData.work[hoveredIndex]} {chartData.unit === 'min' ? 'minut' : 'h'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: IDLE_COLOR }} />
              <Typography sx={{ fontSize: '13px' }}>
                Przestój {chartData.idle[hoveredIndex]} {chartData.unit === 'min' ? 'minut' : 'h'}
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
      </Box>

      {/* przycisk rozwijający listę logów maszyny */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          variant="outlined"
          onClick={() => setLogsOpen((prev) => !prev)}
          endIcon={logsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        >
          {logsOpen ? 'Ukryj logi' : 'Pokaż logi'}
        </Button>
      </Box>

      <Collapse in={logsOpen} unmountOnExit>
        <Box sx={{ mt: 2, mx: 'auto', maxWidth: 900, px: { xs: 2, sm: 0 } }}>
          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data i godzina</TableCell>
                  <TableCell align="right">Ilość ({machine.unit})</TableCell>
                  {machine.hasDimensions && (
                    <>
                      <TableCell align="right">Długość (cm)</TableCell>
                      <TableCell align="right">Średnica (cm)</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingLogs ? (
                  <TableRow>
                    <TableCell colSpan={machine.hasDimensions ? 4 : 2} align="center">
                      Ładowanie…
                    </TableCell>
                  </TableRow>
                ) : pagedLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={machine.hasDimensions ? 4 : 2} align="center">
                      Brak logów w wybranym okresie
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedLogs.map((log, i) => {
                    const dims = machine.hasDimensions
                      ? [log.val_2, log.val_3, log.val_4].filter((v) => v !== null && v !== undefined)
                      : [];
                    const diameter = dims.length > 0 ? dims.reduce((s, v) => s + v, 0) / dims.length / 10 : 0;
                    return (
                      <TableRow key={`${log.created_at}-${i}`}>
                        <TableCell>{dayjs(log.created_at).format('DD-MM-YYYY HH:mm:ss')}</TableCell>
                        <TableCell align="right">{(log[machine.valueColumn] || 0).toFixed(machine.decimals)}</TableCell>
                        {machine.hasDimensions && (
                          <>
                            <TableCell align="right">{((log.val_1 || 0) / 10).toFixed(2)}</TableCell>
                            <TableCell align="right">{diameter.toFixed(2)}</TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {logsTotalCount > LOGS_PAGE_SIZE && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={Math.ceil(logsTotalCount / LOGS_PAGE_SIZE)}
                page={logsPage}
                onChange={(_, value) => setLogsPage(value)}
                color="primary"
              />
            </Box>
          )}
        </Box>
      </Collapse>

    </>
  );
}

export default MachineOperationReport;
